import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Badge, Button, Conductor, Modal, Stars01, Textarea } from "@opendate-app/setlist";

const GENERIC_ERROR = "Couldn't build that segment. Try rephrasing, or build it manually.";
const RATE_LIMIT_ERROR = "You're building segments quickly. Try again in a minute.";

const styles = {
  card: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: "20px 24px 24px",
    borderRadius: 12,
    border: "1px solid var(--sl-border-brand, #2A9CE4)",
    background: "linear-gradient(180deg, var(--sl-bg-brand-primary, #F6FAFE), var(--sl-bg-primary, #fff))",
    boxShadow: "var(--sl-shadow-xs, 0 1px 2px 0 rgba(10, 13, 18, 0.05))",
  },
  head: { display: "flex", gap: 12, alignItems: "flex-start" },
  titleRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  title: { fontSize: 16, lineHeight: "24px", fontWeight: 600, color: "var(--sl-text-primary, #333333)" },
  subtitle: { fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary, #535862)" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  chips: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 },
  tryLabel: { fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary, #717680)", marginRight: 4 },
  chip: { whiteSpace: "normal", textAlign: "left", maxWidth: "100%", borderRadius: 9999 },
};

const AiSegmentPrompt = ({ csrfToken, team, segmentId, examples = [], placeholder = "e.g. Fans who like Rock but haven't bought a ticket this year", hasConditions = false, onBuilt, beforeBuild = () => undefined, onBuildingChange = () => undefined }) => {
  const [prompt, setPrompt] = useState("");
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const build = (text) => {
    const value = text.trim();
    if (!value || phase === "building") return;
    if (hasConditions) {
      setPendingPrompt(value);
      return;
    }

    runBuild(value);
  };

  const confirmReplace = () => {
    const value = pendingPrompt;
    setPendingPrompt(null);
    if (value) runBuild(value);
  };

  const runBuild = (value) => {
    setPhase("building");
    setError(null);
    onBuildingChange(true);
    Promise.resolve(beforeBuild())
      .then((resolvedSegmentId) => {
        if (!mountedRef.current) return null;
        axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
        return axios.post(`/teams/${team.id}/segments/${resolvedSegmentId || segmentId}/ai_build`, { segment_description: value });
      })
      .then((response) => {
        if (!mountedRef.current || !response) return;
        const { data } = response;
        setPhase("idle");
        onBuilt({
          segmentName: data.segment_name,
          fanFilterGroup: data.fan_filter_group,
          prompt: value,
          rationale: data.rationale,
          skippedCount: data.skipped_count || 0,
        });
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setPhase("idle");
        setError(err?.response?.status === 429 ? RATE_LIMIT_ERROR : err?.response?.data?.error || GENERIC_ERROR);
      })
      .finally(() => {
        if (mountedRef.current) onBuildingChange(false);
      });
  };

  const pickExample = (example) => {
    setPrompt(example);
    build(example);
  };

  const building = phase === "building";

  return (
    <section style={styles.card}>
      <div style={styles.head}>
        <Conductor width={36} height={36} animation={building ? "deep-sparkle-breath" : undefined} aria-hidden="true" />
        <div>
          <div style={styles.titleRow}>
            <span style={styles.title}>Describe who you want to reach</span>
            <Badge color="brand" size="sm">Conductor</Badge>
          </div>
          <div style={styles.subtitle}>Write it in plain English. Conductor turns it into conditions you can review and edit.</div>
        </div>
      </div>
      <Textarea
        aria-label="Describe who you want to reach"
        rows={3}
        placeholder={placeholder}
        value={prompt}
        disabled={building}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            build(prompt);
          }
        }}
      />
      <div style={styles.footer}>
        <div style={styles.chips}>
          {examples.length ? <span style={styles.tryLabel}>Try</span> : null}
          {examples.map((example) => (
            <Button key={example} variant="secondary" size="sm" style={styles.chip} disabled={building} onClick={() => pickExample(example)}>
              {example}
            </Button>
          ))}
        </div>
        <Button variant="primary" style={{ marginLeft: "auto" }} icon={building ? undefined : Stars01} disabled={building || !prompt.trim()} onClick={() => build(prompt)}>
          {building ? "Building…" : "Build segment"}
        </Button>
      </div>
      {error ? <Alert color="error" role="alert" title={error} /> : null}
      <Modal
        open={pendingPrompt !== null}
        type="warning"
        size="sm"
        title="Replace the current conditions?"
        description="Building from your description replaces every condition in this segment. You can edit the new conditions afterward."
        confirmLabel="Replace conditions"
        cancelLabel="Keep current"
        onConfirm={confirmReplace}
        onCancel={() => setPendingPrompt(null)}
        onClose={() => setPendingPrompt(null)}
      />
    </section>
  );
};

AiSegmentPrompt.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired }).isRequired,
  segmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  examples: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  hasConditions: PropTypes.bool,
  onBuilt: PropTypes.func.isRequired,
  beforeBuild: PropTypes.func,
  onBuildingChange: PropTypes.func,
};

export default AiSegmentPrompt;
